import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Popover } from "@base-ui/react/popover";
import { KeyValuePairType, TicketPriority, TicketStatus } from "../types/enums";
import type { KeyValuePairDTO } from "../types/dtos";
import type { AddUpdateTicketViewModel } from "../types/viewModels";
import {
  KeyValueViewType,
  type KeyValueItem,
} from "@repo/types/ui/keyValuePairs";
import { useUserKeyValueItems } from "@repo/hooks/queries";
import {
  useAddUpdateTicket,
  useGetKeyValuePair,
  useGetKeyValuePairs,
} from "../hooks/queries";
import {
  getKeyValueMenuButtonRenderer,
  getUserMenuButtonRenderer,
} from "../components/tickets/helpers/TicketFields";
import KeyValueMenu from "../components/ui/menu/KeyValueMenu";
import Input from "../components/form/input/InputField";
import Button from "../components/ui/button/Button";

export default function NewTicket() {
  const [isOpen, setIsOpen] = useState(false);
  const { data: userKeyValues } = useUserKeyValueItems();

  const getKeyValueItems = (
    keyValuePairs: KeyValuePairDTO[],
  ): KeyValueItem[] => {
    return keyValuePairs.map(
      (keyValuePair: KeyValuePairDTO): KeyValueItem => ({
        viewType: KeyValueViewType.textOnly,
        Id: keyValuePair.Id,
        Value: keyValuePair.Value,
        Description: keyValuePair.Description ?? "",
        PairType: keyValuePair.PairType ?? 0,
        PairTypeEnumValue: keyValuePair.PairTypeEnumValue ?? 0,
        BgColorHex: keyValuePair.BgColorHex,
        TextColorHex: keyValuePair.TextColorHex,
        SortOrder: keyValuePair.SortOrder ?? 0,
        classNames: "p-2 mb-2 justify-center",
      }),
    );
  };

  const { data: ticketStatusKeyValueItems } = useGetKeyValuePairs(
    KeyValuePairType.TicketStatus,
    getKeyValueItems,
  );

  const { data: ticketPriorityKeyValueItems } = useGetKeyValuePairs(
    KeyValuePairType.TicketPriority,
    getKeyValueItems,
  );

  const { data: unassignedKeyValuePair } = useGetKeyValuePair(
    KeyValuePairType.TicketStatus,
    TicketStatus.Unassigned,
  );

  const { data: mediumPriorityKeyValuePair } = useGetKeyValuePair(
    KeyValuePairType.TicketPriority,
    TicketPriority.Medium,
  );

  const { mutateAsync: addUpdateTicket } = useAddUpdateTicket();

  const {
    control,
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<AddUpdateTicketViewModel>();

  useEffect(() => {
    if (!unassignedKeyValuePair || !mediumPriorityKeyValuePair) return;

    reset({
      Title: "",
      Description: "",
      AssignedToId: undefined,
      StatusKeyValuePairId: unassignedKeyValuePair.Id,
      PriorityKeyValuePairId: mediumPriorityKeyValuePair.Id,
    });
  }, [unassignedKeyValuePair, mediumPriorityKeyValuePair, reset]);

  async function handleCreateTicket(data: AddUpdateTicketViewModel) {
    await addUpdateTicket(data);
  }

  const handleOpenChange = () => {
    setIsOpen((prev) => {
      const newIsOpen = !prev;

      if (newIsOpen && unassignedKeyValuePair && mediumPriorityKeyValuePair) {
        reset({
          Title: "",
          Description: "",
          AssignedToId: undefined,
          StatusKeyValuePairId: unassignedKeyValuePair.Id,
          PriorityKeyValuePairId: mediumPriorityKeyValuePair.Id,
        });
      }
      return newIsOpen;
    });
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Popover.Trigger
        render={
          <Button variant="outline" size="sm">
            New Ticket
          </Button>
        }
        delay={50}
      ></Popover.Trigger>

      <Popover.Portal>
        <Popover.Positioner align="start" sideOffset={8}>
          <Popover.Popup className="w-96 box-border rounded-lg bg-[canvas] transition-[scale,opacity] duration-150 data-[starting-style]:scale-90 data-[starting-style]:opacity-0 data-[ending-style]:scale-90 data-[ending-style]:opacity-0 shadow-lg shadow-gray-200 outline-1 outline-gray-200">
            <div className="row-start-2 col-start-1 p-4">
              <form>
                <div className="grid grid-rows-6 grid-cols-1 gap-y-2">
                  <div className="">
                    <label className="block text-medium text-gray-700 text-sm">
                      Title
                    </label>

                    <Input
                      placeholder="Title"
                      sizeType="sm"
                      error={!!errors.Title}
                      errorMessage={errors.Title?.message}
                      {...register("Title", {
                        required: "Title is required",
                      })}
                    />
                  </div>

                  <div className="">
                    <label className="block text-medium text-gray-700 text-sm">
                      Description
                    </label>

                    <Input
                      placeholder="Description"
                      sizeType="sm"
                      error={!!errors.Description}
                      errorMessage={errors.Description?.message}
                      {...register("Description", {
                        required: "Description is required",
                      })}
                    />
                  </div>

                  {userKeyValues && (
                    <div className="">
                      <label className="block text-medium text-gray-700 text-sm">
                        Owner
                      </label>

                      <div className="border border-gray-200 p-1 rounded">
                        <Controller
                          name="AssignedToId"
                          control={control}
                          render={({ field: { onChange } }) => (
                            <KeyValueMenu
                              mode="assignee"
                              isMulti={true}
                              items={userKeyValues}
                              align="center"
                              onValueChange={async (value) => {
                                onChange(value);
                              }}
                              triggerRenderer={() =>
                                getUserMenuButtonRenderer(
                                  userKeyValues ?? [],
                                  null,
                                )
                              }
                              maxHeight={300}
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}

                  {ticketStatusKeyValueItems && (
                    <div className="h-10">
                      <label className="block text-medium text-gray-700 text-sm">
                        Status
                      </label>

                      {unassignedKeyValuePair && (
                        <Controller
                          name="StatusKeyValuePairId"
                          control={control}
                          render={({ field }) => (
                            <KeyValueMenu
                              selectedKey={field.value}
                              items={ticketStatusKeyValueItems}
                              align="center"
                              enumType={KeyValuePairType.TicketStatus}
                              onValueChange={async (value) => {
                                field.onChange(value);
                              }}
                              triggerRenderer={() =>
                                getKeyValueMenuButtonRenderer(
                                  ticketStatusKeyValueItems ?? [],
                                  field.value ?? unassignedKeyValuePair.Id,
                                )
                              }
                              maxHeight={300}
                            />
                          )}
                        />
                      )}
                    </div>
                  )}

                  {ticketPriorityKeyValueItems && (
                    <div className="h-10">
                      <label className="block text-medium text-gray-700 text-sm">
                        Priority
                      </label>

                      {mediumPriorityKeyValuePair && (
                        <Controller
                          name="PriorityKeyValuePairId"
                          control={control}
                          render={({ field }) => (
                            <KeyValueMenu
                              selectedKey={field.value}
                              items={ticketPriorityKeyValueItems}
                              align="center"
                              enumType={KeyValuePairType.TicketPriority}
                              onValueChange={async (value) => {
                                field.onChange(value);
                              }}
                              triggerRenderer={() =>
                                getKeyValueMenuButtonRenderer(
                                  ticketPriorityKeyValueItems ?? [],
                                  field.value ?? mediumPriorityKeyValuePair.Id,
                                )
                              }
                              maxHeight={300}
                            />
                          )}
                        />
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-end space-x-2">
                    <div>
                      <Button
                        type="submit"
                        size="sm"
                        onClick={handleSubmit(handleCreateTicket)}
                      >
                        Create Ticket
                      </Button>
                    </div>

                    <div>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleOpenChange}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
